import {
  getConnectableIntegrationToolsForScenario,
  getIntegrationProviderForTool,
  normalizeIntegrationProvider,
  type ConnectableIntegrationScenario,
} from "../Settings/components/integrations/integrationCatalog";

export type OnboardingConnectionCategoryId =
  | "store_ops"
  | "advertising"
  | "email_marketing"
  | "analytics"
  | "seo"
  | "social_media"
  | "customers";

export type OnboardingConnectionBrowseCategoryId =
  OnboardingConnectionCategoryId;

export interface OnboardingConnectionCategory {
  id: OnboardingConnectionCategoryId;
  scenario: ConnectableIntegrationScenario;
}

export interface OnboardingServerIntegrationLike {
  provider?: string | null;
  connected?: boolean | null;
}

export interface ResolveOnboardingConnectionRequirementsInput {
  serverIntegrations?: readonly OnboardingServerIntegrationLike[];
  persistedConnectedToolNames?: readonly string[];
  serverHydrated: boolean;
}

export interface OnboardingConnectionCategoryState extends OnboardingConnectionCategory {
  tools: string[];
  connectedTools: string[];
  count: number;
}

export interface OnboardingConnectionRequirementsState {
  categories: Record<
    OnboardingConnectionCategoryId,
    OnboardingConnectionCategoryState
  >;
  connectedTools: string[];
  connectedCount: number;
  requiredConnectionCount: number;
  remainingConnectionCount: number;
  meetsConnectionCount: boolean;
}

export type OnboardingPlanStepAccessState =
  | "skipped"
  | "checking-connections"
  | "blocked"
  | "ready";

export interface CanContinueOnboardingConnectionStepInput {
  requirements: OnboardingConnectionRequirementsState;
  hasFreshServerSnapshot: boolean;
  hasPendingAccountSelection?: boolean;
  hasConnectionOperationInFlight?: boolean;
}

export const ONBOARDING_MIN_CONNECTION_COUNT = 4;

export const ONBOARDING_CONNECTION_CATEGORIES: readonly OnboardingConnectionCategory[] =
  [
    {
      id: "store_ops",
      scenario: "Store Ops",
    },
    {
      id: "advertising",
      scenario: "Advertising",
    },
    {
      id: "email_marketing",
      scenario: "Email Marketing",
    },
    {
      id: "analytics",
      scenario: "Analytics",
    },
    {
      id: "seo",
      scenario: "SEO",
    },
    {
      id: "social_media",
      scenario: "Social Media",
    },
    {
      id: "customers",
      scenario: "Customers",
    },
  ];

export const getOnboardingConnectionToolsForScenario = (
  scenario: ConnectableIntegrationScenario,
): string[] => getConnectableIntegrationToolsForScenario(scenario);

const normalizeToolName = (toolName?: string | null): string =>
  String(toolName || "")
    .trim()
    .toLowerCase();

const getConnectedServerProviders = (
  serverIntegrations: readonly OnboardingServerIntegrationLike[],
): Set<string> =>
  new Set(
    serverIntegrations
      .filter((integration) => integration.connected === true)
      .map((integration) => normalizeIntegrationProvider(integration.provider))
      .filter(Boolean),
  );

const resolveCategoryState = (
  category: OnboardingConnectionCategory,
  connectedServerProviders: ReadonlySet<string>,
  persistedConnectedTools: ReadonlySet<string>,
  serverHydrated: boolean,
): OnboardingConnectionCategoryState => {
  const tools = getOnboardingConnectionToolsForScenario(category.scenario);
  const connectedTools = tools.filter((tool) => {
    const provider = getIntegrationProviderForTool(tool);
    const isConnectedOnServer = provider
      ? connectedServerProviders.has(normalizeIntegrationProvider(provider))
      : false;

    if (serverHydrated) {
      return isConnectedOnServer;
    }

    return (
      isConnectedOnServer ||
      persistedConnectedTools.has(normalizeToolName(tool))
    );
  });
  const count = connectedTools.length;

  return {
    ...category,
    tools,
    connectedTools,
    count,
  };
};

export const resolveOnboardingConnectionRequirements = ({
  serverIntegrations = [],
  persistedConnectedToolNames = [],
  serverHydrated,
}: ResolveOnboardingConnectionRequirementsInput): OnboardingConnectionRequirementsState => {
  const connectedServerProviders =
    getConnectedServerProviders(serverIntegrations);
  const persistedConnectedTools = new Set(
    persistedConnectedToolNames.map(normalizeToolName).filter(Boolean),
  );
  const categories = ONBOARDING_CONNECTION_CATEGORIES.reduce(
    (states, category) => {
      states[category.id] = resolveCategoryState(
        category,
        connectedServerProviders,
        persistedConnectedTools,
        serverHydrated,
      );
      return states;
    },
    {} as Record<
      OnboardingConnectionCategoryId,
      OnboardingConnectionCategoryState
    >,
  );
  const connectedTools = Array.from(
    new Set(
      ONBOARDING_CONNECTION_CATEGORIES.flatMap(
        (category) => categories[category.id].connectedTools,
      ),
    ),
  );
  const connectedCount = connectedTools.length;
  const remainingConnectionCount = Math.max(
    0,
    ONBOARDING_MIN_CONNECTION_COUNT - connectedCount,
  );

  return {
    categories,
    connectedTools,
    connectedCount,
    requiredConnectionCount: ONBOARDING_MIN_CONNECTION_COUNT,
    remainingConnectionCount,
    meetsConnectionCount: remainingConnectionCount === 0,
  };
};

export const canContinueOnboardingConnectionStep = ({
  requirements,
  hasFreshServerSnapshot,
  hasPendingAccountSelection = false,
  hasConnectionOperationInFlight = false,
}: CanContinueOnboardingConnectionStepInput): boolean =>
  hasFreshServerSnapshot &&
  requirements.meetsConnectionCount &&
  !hasPendingAccountSelection &&
  !hasConnectionOperationInFlight;

export const resolveOnboardingPlanStepAccess = ({
  requirements,
  hasFreshServerSnapshot,
  shouldSkipConnectionStep,
}: {
  requirements: OnboardingConnectionRequirementsState;
  hasFreshServerSnapshot: boolean;
  shouldSkipConnectionStep: boolean;
}): OnboardingPlanStepAccessState => {
  if (shouldSkipConnectionStep) {
    return "skipped";
  }

  if (!hasFreshServerSnapshot) {
    return "checking-connections";
  }

  return requirements.meetsConnectionCount ? "ready" : "blocked";
};

export const resolveOnboardingStepForRendering = ({
  displayStep,
  planStepAccessState,
}: {
  displayStep: number;
  planStepAccessState: OnboardingPlanStepAccessState;
}): number => {
  return displayStep === 2 && planStepAccessState === "blocked"
    ? 1
    : displayStep;
};
